import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'rounded-[8px] flex items-center justify-center flex-row disabled:opacity-50 disabled:cursor-not-allowed transition-all ',
  {
    variants: {
      variant: {
        default:
          'bg-primary-cta active:scale-95 text-primary-black hover:opacity-80',
        outline:
          'bg-transparent text-secondary-white-vei border-[#6b6b6b] rounded-[8px] border  hover:text-secondary-white-vei/50 hover:border-secondary-white-vei/50 disabled:text-[#565656] disabled:border-[#565656]',
        miniOutline:
          'border border-[#3C444B] text-[#767676] hover:opacity-80 active:scale-95',
        accent:
          'bg-background border border-background text-[#ACACAC] hover:bg-[#282828]',
        'accent-active':
          'bg-[#565656] border border-[#767676] text-secondary-white-vei hover:opacity-90',
        secondary:
          'bg-[#222222] text-[#B0927A] hover:opacity-80 active:scale-95',
        ghost:
          'bg-transparent text-secondary-white-vei  hover:opacity-80 active:scale-95',
        cat: 'font-semibold bg-primary-cta disabled:bg-[#645543] disabled:opacity-100 text-primary-black shadow-[4px_4px_0px_0px_rgba(44,42,41,1)]  disabled:translate-x-[2px] disabled:translate-y-[2px] disabled:shadow-[2px_2px_0px_0px_rgba(44,42,41,1)] hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_rgba(44,42,41,1)] active:shadow-[2px_2px_0px_0px_rgba(44,42,41,1)] active:translate-x-[2px] active:translate-y-[2px]',
        'cat-blue':
          'bg-primary-yes text-primary-black shadow-[4px_4px_0px_0px_rgba(31,29,28,1)]  disabled:translate-x-[2px] disabled:translate-y-[2px] disabled:shadow-[2px_2px_0px_0px_rgba(31,29,28,1)] hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_rgba(31,29,28,1)] active:shadow-[2px_2px_0px_0px_rgba(31,29,28,1)] active:translate-x-[2px] disabled:opacity-100 disabled:bg-[#80582d] active:translate-y-[2px]',
        'cat-green':
          'bg-primary-no text-primary-black shadow-[4px_4px_0px_0px_rgba(31,29,28,1)]  disabled:translate-x-[2px] disabled:translate-y-[2px] disabled:shadow-[2px_2px_0px_0px_rgba(31,29,28,1)] hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_rgba(31,29,28,1)] active:shadow-[2px_2px_0px_0px_rgba(31,29,28,1)] active:translate-x-[2px] disabled:opacity-100 disabled:bg-[#344f60] active:translate-y-[2px]',
      },
      size: {
        default: 'h-[50px] px-[10px]',
        icon: 'size-[50px]',
        medium: 'h-[32px] px-[10px]',
        mini: 'size-[32px]',
      },
      text: {
        16: 'text-[16px]',
        20: 'text-[20px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      text: 16,
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, text, children, asChild = false, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, text, className }))}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
